/**
 * OpenAI Realtime (WebRTC) voice UI — experimental SC3 path.
 */

import { tokens } from '@healthdesk/shared-utils';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PersonIcon from '@mui/icons-material/Person';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

import useOpenAIRealtimeVoiceConversationEngine from './useOpenAIRealtimeVoiceConversationEngine';
import useAudioLevel from './useAudioLevel';
import VoiceLevelIndicator from './VoiceLevelIndicator';
import {
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
} from '../../features/patient/components/symptomCheck/voice-conversation';

function OpenAIRealtimeVoiceConversationEngine({
  onPauseDetected,
  title = 'Voice Conversation',
  openingMessage = 'Hello! How can I help you today?',
  maxQuestions = 0,
  language = 'en-US',
  onLanguageChange,
  compact = false,
  showLanguageSelector = false,
  onComplete,
  onStartRequested,
  onSkip,
  context,
  onUserSpeech,
  onLogEntry,
  onLiveTranscript,
  autoStart = false,
  askFirstQuestionAfterOpening = false,
  askDisclaimerAfterOpening = true,
  /** Reserved for future Realtime voice selection (server/session config). */
  liveSpeechVoice: _liveSpeechVoice,
  headerExtra,
  beforeStart,
  conversationLog: controlledConversationLog,
  onConversationLogChange,
  questionsAsked: controlledQuestionsAsked,
  onQuestionsAskedChange,
}) {
  const languageLabelId = useId();
  const engine = useOpenAIRealtimeVoiceConversationEngine({
    onPauseDetected,
    openingMessage,
    language,
    context,
    onComplete,
    onUserSpeech,
    onLogEntry,
    onLiveTranscript,
    askFirstQuestionAfterOpening,
    askDisclaimerAfterOpening,
    controlledConversationLog,
    onConversationLogChange,
    controlledQuestionsAsked,
    onQuestionsAskedChange,
  });

  const {
    isActive,
    isStarting,
    isListening,
    isSpeaking,
    questionsAsked,
    conversationLog,
    audioConfirmed,
    isAcceptingInput,
    speechSupported,
    error,
    startConversation,
    stopConversation,
    endConversationAcknowledged,
    conversationScrollRef,
  } = engine;

  const lastAssistantLine = [...conversationLog].reverse().find((e) => e.speaker === 'assistant');
  const currentQuestion = lastAssistantLine?.text || '';

  const audioLevel = useAudioLevel(
    isActive && isListening && audioConfirmed && isAcceptingInput && !isSpeaking
  );
  const prevIsActiveRef = useRef(isActive);
  const [showSessionStoppedNotice, setShowSessionStoppedNotice] = useState(false);

  useEffect(() => {
    if (autoStart && !isActive) {
      const canStart = typeof onStartRequested === 'function' ? onStartRequested() : true;
      if (canStart !== false) startConversation();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartClick = useCallback(() => {
    const canStart = typeof onStartRequested === 'function' ? onStartRequested() : true;
    if (canStart !== false) startConversation();
  }, [onStartRequested, startConversation]);

  useEffect(() => {
    if (conversationScrollRef.current) {
      requestAnimationFrame(() => {
        if (conversationScrollRef.current) {
          conversationScrollRef.current.scrollTop = conversationScrollRef.current.scrollHeight;
        }
      });
    }
  }, [conversationLog, conversationScrollRef]);

  useEffect(() => {
    const wasActive = prevIsActiveRef.current;
    prevIsActiveRef.current = isActive;
    if (wasActive && !isActive) {
      setShowSessionStoppedNotice(true);
      const id = window.setTimeout(() => {
        setShowSessionStoppedNotice(false);
      }, 2000);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [isActive]);

  if (!speechSupported) {
    return (
      <Box sx={{ p: compact ? 2 : 3, textAlign: 'center', bgcolor: 'warning.lighter', borderRadius: 2 }}>
        <Typography color="warning.dark" variant={compact ? 'body2' : 'body1'}>
          OpenAI Realtime needs microphone access and a supported browser (Chrome, Edge, or Safari).
        </Typography>
      </Box>
    );
  }

  const progress = maxQuestions > 0 ? Math.min(100, (questionsAsked.length / maxQuestions) * 100) : 0;

  const content = (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={compact ? 1 : 2}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={600}>
          {title}
        </Typography>
        {isActive && (
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
            <Chip icon={<GraphicEqIcon />} label="OpenAI Realtime" color="secondary" size="small" variant="outlined" />
            {isStarting && !isSpeaking && (
              <Chip
                icon={<VolumeUpIcon />}
                label="Connecting..."
                color="info"
                size="small"
                sx={{ animation: 'pulse 0.8s infinite' }}
              />
            )}
            {isListening && audioConfirmed && isAcceptingInput && !isStarting && (
              <Chip
                icon={<MicIcon />}
                label={compact ? 'Live' : 'Listening'}
                color="success"
                size="small"
                sx={{ animation: 'pulse 1.5s infinite' }}
              />
            )}
            {isSpeaking && (
              <Chip icon={<VolumeUpIcon />} label={compact ? 'Speaking' : 'Speaking...'} color="primary" size="small" />
            )}
          </Box>
        )}
      </Box>

      {headerExtra}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {showSessionStoppedNotice && !isActive ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Realtime session stopped.
        </Alert>
      ) : null}

      {isActive && (
        <Box sx={{ mb: compact ? 1 : 2 }}>
          <LinearProgress
            variant={isStarting || isSpeaking || (isListening && !audioConfirmed) ? 'indeterminate' : 'determinate'}
            value={
              maxQuestions > 0 && isListening && audioConfirmed && !isStarting
                ? progress
                : isListening && audioConfirmed && !isStarting
                  ? 100
                  : 0
            }
            color={isStarting ? 'info' : isSpeaking ? 'primary' : isListening && audioConfirmed ? 'success' : 'warning'}
            sx={{ height: compact ? 3 : 4, borderRadius: 2 }}
          />
          {maxQuestions > 0 && isListening && audioConfirmed && !isStarting && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              Question {Math.min(questionsAsked.length, maxQuestions)} of {maxQuestions}
            </Typography>
          )}
        </Box>
      )}

      {isActive && !isStarting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: compact ? 1 : 1.5 }}>
          <VoiceLevelIndicator
            level={audioLevel}
            muted={isSpeaking || !isAcceptingInput}
            height={compact ? 22 : 28}
            userHasSpoken={conversationLog.some((e) => e.speaker === 'user')}
          />
        </Box>
      )}

      {conversationLog.length > 0 && (
        <Box
          ref={conversationScrollRef}
          sx={{
            maxHeight: compact ? 150 : 200,
            overflow: 'auto',
            mb: compact ? 1 : 2,
            p: compact ? 1 : 1.5,
            bgcolor: 'background.paper',
            borderRadius: 2,
            flex: compact ? 1 : 'none',
          }}
        >
          {conversationLog.map((entry, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
                opacity: index === conversationLog.length - 1 ? 1 : 0.7,
              }}
            >
              {entry.speaker === 'user' ? (
                <PersonIcon fontSize="small" color="action" />
              ) : (
                <VolumeUpIcon fontSize="small" color="primary" />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontStyle: entry.speaker === 'assistant' ? 'italic' : 'normal',
                  color: entry.speaker === 'assistant' ? 'primary.main' : 'text.primary',
                }}
              >
                {entry.text}
              </Typography>
            </Box>
          ))}
          {!isActive && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              Conversation has ended.
            </Typography>
          )}
        </Box>
      )}

      <Fade in={!!currentQuestion && isActive}>
        <Box sx={{ mb: compact ? 1 : 2, p: compact ? 1.5 : 2, bgcolor: 'secondary.lighter', borderRadius: 2 }}>
          <Typography variant={compact ? 'body2' : 'body1'} color="secondary.dark" fontWeight={500}>
            &quot;{currentQuestion}&quot;
          </Typography>
        </Box>
      </Fade>

      {!isActive && conversationLog.length === 0 && beforeStart}

      {!isActive && conversationLog.length === 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          {showLanguageSelector ? (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id={languageLabelId}>Language</InputLabel>
              <Select
                labelId={languageLabelId}
                value={language}
                label="Language"
                onChange={(e) => onLanguageChange?.(e.target.value)}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {getLanguageByCode(language)?.label || language} — session language hint
            </Typography>
          )}
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            startIcon={<MicIcon />}
            onClick={handleStartClick}
            sx={{
              px: compact ? 3 : 4,
              py: compact ? 1 : 1.5,
              borderRadius: 3,
              fontSize: compact ? '0.95rem' : '1.1rem',
            }}
          >
            {compact ? 'Start' : 'Start Realtime Voice'}
          </Button>
        </Box>
      )}

      <Box display="flex" justifyContent="center" gap={2} sx={{ mt: compact ? 1 : 0 }}>
        {isActive && (isAcceptingInput || endConversationAcknowledged) && !isStarting && (
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            startIcon={<StopIcon />}
            onClick={stopConversation}
            aria-pressed={endConversationAcknowledged}
            sx={{
              px: compact ? 3 : 4,
              py: compact ? 1 : 1.5,
              borderRadius: 3,
              bgcolor: tokens.colors.msiRed.light,
              '&:hover': { bgcolor: '#6b1538' },
              ...(endConversationAcknowledged && {
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.35)',
                transform: 'translateY(1px)',
              }),
            }}
          >
            End Conversation
          </Button>
        )}
        {onSkip && !isActive && conversationLog.length === 0 && (
          <Button
            variant="outlined"
            color="inherit"
            size={compact ? 'medium' : 'large'}
            onClick={onSkip}
            sx={{ px: compact ? 3 : 4, py: compact ? 1 : 1.5, borderRadius: 3 }}
          >
            Skip
          </Button>
        )}
      </Box>

      {!isActive && conversationLog.length === 0 && !compact && (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
          Experimental OpenAI Realtime (WebRTC). User audio is processed by OpenAI; scripted questions are injected
          after each transcribed turn via the same host logic as Classic voice.
        </Typography>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
      `}</style>
    </>
  );

  if (compact) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {content}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        ...contentCardSx,
        p: 3,
        background: isActive
          ? 'linear-gradient(135deg, #ede7f6 0%, #e8f5e9 100%)'
          : 'background.paper',
        transition: 'background 0.3s ease',
      }}
    >
      {content}
    </Paper>
  );
}

export default OpenAIRealtimeVoiceConversationEngine;
